import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fab,
  Tabs,
  Tab,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Storefront as StoreIcon,
  ViewModule as GridIcon,
  List as ListIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';
import { API_BASE_URL } from '../../config';

interface StoreItem {
  assetId: string;
  assetName: string;
  assetFileName: string;
  stockLevel: number;
  previewPath?: string;
  searchText?: string;
  isEditing?: boolean;
  newStockLevel?: number;
}

interface ImportResult {
  updated: number;
  skipped: number;
  errors: number;
  skippedDetails: Array<{ row: number; reason: string; assetId?: string }>;
  errorDetails: Array<{ row: number; reason: string }>;
}

const normalizeInventorySearchText = (value: unknown): string =>
  String(value ?? '').toLocaleLowerCase('sv-SE').replace(/[._-]+/g, ' ');

const InventoryManagement: React.FC = () => {
  const [storeItems, setStoreItems] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list'); // Default till list
  const [hasChanges, setHasChanges] = useState(false);
  const [isEditingAll, setIsEditingAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Export / Import state
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ladda butiksmaterial
  const fetchStoreItems = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/inventory/stock-levels`);
      const items = response.data.map((item: any) => ({
        ...item,
        isEditing: false,
        newStockLevel: item.stockLevel
      }));
      setStoreItems(items);
    } catch (error) {
      console.error('Error fetching store items:', error);
      setError('Kunde inte ladda butiksmaterial');
    } finally {
      setLoading(false);
    }
  };

  // Starta redigering av lagersaldo
  const startEditing = (assetId: string) => {
    setStoreItems(items => 
      items.map(item => 
        item.assetId === assetId 
          ? { ...item, isEditing: true, newStockLevel: item.stockLevel }
          : item
      )
    );
  };

  // Avbryt redigering
  const cancelEditing = (assetId: string) => {
    setStoreItems(items => 
      items.map(item => 
        item.assetId === assetId 
          ? { ...item, isEditing: false, newStockLevel: item.stockLevel }
          : item
      )
    );
    checkForChanges();
  };

  // Uppdatera temporärt lagersaldo
  const updateStockLevel = (assetId: string, newStockLevel: number) => {
    setStoreItems(items => 
      items.map(item => 
        item.assetId === assetId 
          ? { ...item, newStockLevel: Math.max(0, newStockLevel) }
          : item
      )
    );
    setHasChanges(true);
  };

  // Bekräfta ändring av lagersaldo
  const confirmStockLevel = async (assetId: string) => {
    const item = storeItems.find((i) => i.assetId === assetId);
    const nextStockLevel = Math.max(0, item?.newStockLevel ?? 0);

    try {
      setSaving(true);
      setError(null);

      await axiosInstance.post(`/api/inventory/batch-update`, {
        updates: [{ assetId, stockLevel: nextStockLevel }],
      });

      setStoreItems((items) =>
        items.map((i) =>
          i.assetId === assetId
            ? { ...i, stockLevel: nextStockLevel, newStockLevel: nextStockLevel, isEditing: false }
            : i
        )
      );
      setSuccess('Lagersaldo uppdaterat!');
    } catch (error: any) {
      console.error('Error saving stock level:', error);
      setError(`Fel vid sparande: ${error.response?.data?.error || error.message}`);
      // Leave row in edit mode so user can retry or cancel
      setStoreItems((items) =>
        items.map((i) => (i.assetId === assetId ? { ...i, isEditing: true } : i))
      );
    } finally {
      setSaving(false);
    }
  };

  // Kontrollera om det finns ändringar
  const checkForChanges = () => {
    const hasModifications = storeItems.some(item => 
      item.stockLevel !== item.newStockLevel || item.isEditing
    );
    setHasChanges(hasModifications);
  };

  // ÄNDRA ALLA - redigera alla artiklar samtidigt
  const editAllItems = () => {
    setStoreItems(items => 
      items.map(item => ({
        ...item,
        isEditing: true,
        newStockLevel: item.stockLevel
      }))
    );
    setIsEditingAll(true);
  };

  // Avsluta redigering för alla artiklar
  const finishEditingAll = () => {
    setStoreItems(items => 
      items.map(item => ({
        ...item,
        isEditing: false
      }))
    );
    setIsEditingAll(false);
  };

  // Återställ alla ändringar
  const resetChanges = () => {
    setStoreItems(items => 
      items.map(item => ({
        ...item,
        newStockLevel: item.stockLevel,
        isEditing: false
      }))
    );
    setHasChanges(false);
    setIsEditingAll(false);
  };

  // Exportera lagersaldo som CSV
  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axiosInstance.get(`/api/inventory/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `lagersaldo_${date}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Kunde inte exportera: ' + (err.response?.data?.error || err.message));
    } finally {
      setExporting(false);
    }
  };

  // Importera CSV
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so same file can be picked again
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      setImporting(true);
      setError(null);
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post(`/api/inventory/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setImportResult(response.data);
      // Refresh list after import
      await fetchStoreItems();
    } catch (err: any) {
      setError('Import misslyckades: ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
    }
  };

  // Spara alla ändringar till servern
  const saveAllChanges = async () => {
    try {
      setSaving(true);
      
      // Samla alla ändringar
      const updates = storeItems
        .filter(item => item.stockLevel !== item.newStockLevel)
        .map(item => ({
          assetId: item.assetId,
          stockLevel: item.newStockLevel || 0
        }));

      if (updates.length === 0) {
        setError('Inga ändringar att spara');
        return;
      }

      console.log('Sending updates to backend:', updates);

      // Skicka batch-uppdatering till servern
      const response = await axiosInstance.post(`/api/inventory/batch-update`, { updates });
      
      console.log('Backend response:', response.data);
      
      setSuccess(`${updates.length} produkter uppdaterade!`);
      setHasChanges(false);
      setIsEditingAll(false);
      
      // Hämta uppdaterad data från servern
      await fetchStoreItems();
    } catch (error: any) {
      console.error('Error saving changes:', error);
      setError(`Fel vid sparande: ${error.response?.data?.error || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchStoreItems();
  }, []);

  useEffect(() => {
    checkForChanges();
  }, [storeItems]);

  const filteredStoreItems = useMemo(() => {
    const searchTerms = normalizeInventorySearchText(searchQuery)
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (searchTerms.length === 0) {
      return storeItems;
    }

    return storeItems.filter((item) => {
      const searchableText = normalizeInventorySearchText([
        item.assetName,
        item.assetFileName,
        item.searchText,
      ].join(' '));

      return searchTerms.every((term) => searchableText.includes(term));
    });
  }, [searchQuery, storeItems]);

  // Rensa meddelanden efter 5 sekunder
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Visa kort-grid vy
  const renderGridView = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
      {filteredStoreItems.map((item) => (
        <Card key={item.assetId} sx={{ 
          position: 'relative',
          border: item.isEditing ? '2px solid #1976d2' : '1px solid #e0e0e0',
          transition: 'all 0.2s'
        }}>
          {item.previewPath && (
            <CardMedia
              component="img"
              height="180"
              image={`${API_BASE_URL}${item.previewPath}`}
              alt={item.assetName}
              sx={{ objectFit: 'cover' }}
            />
          )}
          
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom noWrap>
              {item.assetName}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
              {item.assetFileName}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Lagersaldo:
              </Typography>
              
              {item.isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    type="number"
                    value={item.newStockLevel || 0}
                    onChange={(e) => updateStockLevel(item.assetId, parseInt(e.target.value) || 0)}
                    size="small"
                    inputProps={{ 
                      min: 0, 
                      style: { textAlign: 'center', width: '80px' },
                      tabIndex: 0
                    }}
                    sx={{ width: '100px' }}
                    autoFocus={isEditingAll}
                  />
                  <IconButton
                    onClick={() => confirmStockLevel(item.assetId)}
                    color="primary"
                    size="small"
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => cancelEditing(item.assetId)}
                    color="error"
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={item.stockLevel} 
                    color={item.stockLevel > 0 ? 'success' : 'error'}
                    size="small"
                  />
                  <IconButton
                    onClick={() => startEditing(item.assetId)}
                    size="small"
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  // Visa tabell-lista vy
  const renderListView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Förhandsvisning</strong></TableCell>
            <TableCell><strong>Produktnamn</strong></TableCell>
            <TableCell><strong>Filnamn</strong></TableCell>
            <TableCell><strong>Lagersaldo</strong></TableCell>
            <TableCell><strong>Åtgärder</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredStoreItems.map((item) => (
            <TableRow 
              key={item.assetId}
              sx={{ 
                backgroundColor: item.isEditing ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                '&:hover': { backgroundColor: item.isEditing ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <TableCell>
                {item.previewPath ? (
                  <Avatar
                    src={`${API_BASE_URL}${item.previewPath}`}
                    alt={item.assetName}
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  />
                ) : (
                  <Avatar variant="rounded" sx={{ width: 60, height: 60 }}>
                    <StoreIcon />
                  </Avatar>
                )}
              </TableCell>
              
              <TableCell>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {item.assetName}
                </Typography>
              </TableCell>
              
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {item.assetFileName}
                </Typography>
              </TableCell>
              
              <TableCell>
                {item.isEditing ? (
                  <TextField
                    type="number"
                    value={item.newStockLevel || 0}
                    onChange={(e) => updateStockLevel(item.assetId, parseInt(e.target.value) || 0)}
                    size="small"
                    inputProps={{ 
                      min: 0, 
                      style: { textAlign: 'center', width: '80px' },
                      tabIndex: 0
                    }}
                    sx={{ width: '100px' }}
                    autoFocus={isEditingAll}
                  />
                ) : (
                  <Chip 
                    label={item.stockLevel} 
                    color={item.stockLevel > 0 ? 'success' : 'error'}
                    size="small"
                  />
                )}
              </TableCell>
              
              <TableCell>
                {item.isEditing ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      onClick={() => confirmStockLevel(item.assetId)}
                      color="primary"
                      size="small"
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => cancelEditing(item.assetId)}
                      color="error"
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <IconButton
                    onClick={() => startEditing(item.assetId)}
                    size="small"
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
          <StoreIcon sx={{ mr: 2, color: 'primary.main' }} />
          Lagerhantering - Butiksmaterial
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Export / Import */}
          <Button
            variant="outlined"
            onClick={handleExport}
            startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
            disabled={exporting || storeItems.length === 0}
            size="small"
          >
            Exportera CSV
          </Button>
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            startIcon={importing ? <CircularProgress size={16} /> : <FileUploadIcon />}
            disabled={importing}
            size="small"
          >
            Importera CSV
          </Button>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />

          {/* Ändra alla knapp */}
          {!isEditingAll ? (
            <Button
              variant="outlined"
              onClick={editAllItems}
              startIcon={<EditIcon />}
            >
              Ändra alla
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={finishEditingAll}
              startIcon={<CheckIcon />}
            >
              Avsluta redigering
            </Button>
          )}
          
          <Tabs
            value={viewMode}
            onChange={(_, newValue) => setViewMode(newValue)}
            sx={{ minHeight: 'auto' }}
          >
            <Tab 
              icon={<GridIcon />} 
              value="grid" 
              label="Grid" 
              sx={{ minHeight: 'auto', py: 1 }}
            />
            <Tab 
              icon={<ListIcon />} 
              value="list" 
              label="Lista" 
              sx={{ minHeight: 'auto', py: 1 }}
            />
          </Tabs>
        </Box>
      </Box>

      <TextField
        fullWidth
        size="small"
        label="Sök butiksmaterial"
        placeholder="Sök på titel, filnamn, artikelnummer, beskrivning eller typ av marknadsmaterial"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        sx={{ mb: 2 }}
        helperText={searchQuery.trim() ? `${filteredStoreItems.length} av ${storeItems.length} butiksmaterial visas` : ' '}
      />

      {/* Meddelanden */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Laddning */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Innehåll */}
      {!loading && (
        <>
          {viewMode === 'grid' ? renderGridView() : renderListView()}
          
          {filteredStoreItems.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                {storeItems.length === 0 ? 'Inga butiksmaterial hittades' : 'Inga butiksmaterial matchar sökningen'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Spara-knapp (flytande) */}
      {hasChanges && (
        <Fab
          color="primary"
          onClick={saveAllChanges}
          disabled={saving}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: 1000
          }}
        >
          {saving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
        </Fab>
      )}

      {/* Återställ-knapp */}
      {hasChanges && (
        <Button
          variant="outlined"
          color="error"
          onClick={resetChanges}
          sx={{
            position: 'fixed',
            bottom: 100,
            right: 32,
            zIndex: 1000
          }}
        >
          Återställ
        </Button>
      )}

      {/* Import-resultatdialog */}
      <Dialog open={importResult !== null} onClose={() => setImportResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Importresultat
        </DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              <Alert
                severity={importResult.errors > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                {importResult.updated} uppdaterade&nbsp;·&nbsp;
                {importResult.skipped} hoppade över&nbsp;·&nbsp;
                {importResult.errors} fel
              </Alert>

              {importResult.skippedDetails.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Hoppade över:</Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mb: 1 }}>
                    <List dense disablePadding>
                      {importResult.skippedDetails.map((s, i) => (
                        <React.Fragment key={i}>
                          <ListItem>
                            <ListItemText
                              primary={`Rad ${s.row}: ${s.reason}`}
                              secondary={s.assetId || undefined}
                              primaryTypographyProps={{ fontSize: '0.8rem' }}
                              secondaryTypographyProps={{ fontSize: '0.72rem', fontFamily: 'monospace' }}
                            />
                          </ListItem>
                          {i < importResult.skippedDetails.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                </>
              )}

              {importResult.errorDetails.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Fel:</Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {importResult.errorDetails.map((e, i) => (
                        <React.Fragment key={i}>
                          <ListItem>
                            <ListItemText
                              primary={`Rad ${e.row}: ${e.reason}`}
                              primaryTypographyProps={{ fontSize: '0.8rem', color: 'error.main' }}
                            />
                          </ListItem>
                          {i < importResult.errorDetails.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)} variant="contained">Stäng</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryManagement; 